import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';
import { useExchangeAuthorizationCodeMutation } from '../../__generated__/apollo-hooks';
import { safeSessionStorage } from '../../helpers/local-storage.helper';
import type { PendingAuthData } from '../../hooks/scm-authorization.constants';
import {
  DEFAULT_CALLBACK_PARAMETERS,
  SCM_AUTH_SESSION_KEY
} from '../../hooks/scm-authorization.constants';

type AuthSuccessMessage = {
  type: 'scm-auth-success';
  origin: string;
};

type AuthErrorMessage = {
  type: 'scm-auth-error';
  origin: string;
  error: string;
};

type AuthMessage = AuthSuccessMessage | AuthErrorMessage;

/**
 * Sends a postMessage to the parent window that opened this popup.
 */
const sendMessageToOpener = (message: AuthMessage): boolean => {
  if (!window.opener) {
    return false;
  }
  try {
    window.opener.postMessage(message, window.location.origin);
    return true;
  } catch {
    return false;
  }
};

/**
 * Reads the pending auth data the opener stashed before launching the popup.
 *
 * The opener writes this *after* calling `window.open`, and a popup's
 * `sessionStorage` is a snapshot taken at open time — so the popup's own store
 * won't carry the opener's later write. We still check it first (a cheap,
 * harmless fallback in case that assumption ever doesn't hold), then fall back
 * to the opener's `sessionStorage`, which is readable here because the OAuth
 * redirect lands the popup back on the app's own (same) origin. OAuth 2.0
 * survives without this (its authorizationId rides in the `state` param), but
 * OAuth 1.0a (e.g. Bitbucket Data Center) has no `state` and depends on it.
 */
const readPendingAuthData = (): PendingAuthData | null => {
  // Our own store goes through the helper. The opener's cannot: reading another
  // window's `.sessionStorage` property throws a SecurityError once it has gone
  // away or become cross-origin, which only a local try/catch can absorb.
  const candidates = [
    () => safeSessionStorage.getItem(SCM_AUTH_SESSION_KEY),
    () => window.opener?.sessionStorage?.getItem(SCM_AUTH_SESSION_KEY)
  ];
  for (const read of candidates) {
    try {
      const raw = read();
      if (raw) {
        return JSON.parse(raw) as PendingAuthData;
      }
    } catch {
      // Cross-origin opener or malformed JSON — try the next candidate.
    }
  }
  return null;
};

/**
 * Decodes the OAuth state parameter to extract the authorizationId.
 * The state is base64-encoded JSON containing the id (authorizationId).
 */
const decodeAuthorizationIdFromState = (state: string): string | null => {
  try {
    const decoded = atob(state);
    const parsed = JSON.parse(decoded) as { id?: string };
    return parsed.id ?? null;
  } catch {
    return null;
  }
};

/**
 * Handles OAuth callbacks when the app is loaded in a popup context.
 *
 * This component should be mounted at the app level to handle SCM authorization
 * callbacks on any page. It detects when the page is opened as a popup with
 * authorization parameters and completes the OAuth flow.
 *
 * This allows SCM providers to redirect to just the origin (e.g., http://localhost:3000)
 * rather than requiring a specific callback path, which simplifies OAuth configuration.
 */
export const ScmCallbackHandler: FunctionComponent = () => {
  const router = useRouter();
  const hasExchangedRef = useRef(false);
  const [exchangeCode] = useExchangeAuthorizationCodeMutation();

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    // Only handle callback if we're in a popup context
    if (!window.opener) {
      return;
    }

    // Prevent double execution in strict mode
    if (hasExchangedRef.current) {
      return;
    }

    const pendingAuth = readPendingAuthData();

    // When a real callback can't be completed, tell the opener so it stops
    // waiting, rather than silently no-opping and leaving it spinning until its
    // timeout fires. `hasExchangedRef` is set so Strict Mode's double-invoke
    // doesn't post twice.
    const reportLostAuthSession = () => {
      hasExchangedRef.current = true;
      sendMessageToOpener({
        type: 'scm-auth-error',
        origin: '',
        error: 'Authorization session was lost. Please try connecting again.'
      });
    };

    // OAuth 1.0a (Bitbucket Data Center) carries no `state`, so the
    // authorizationId is only available from the data the opener stashed. Its
    // callback params (`oauth_token`/`oauth_verifier`) also don't match the
    // default `callbackParameters`, so a missing record would otherwise fall
    // through to the silent `hasAllParams` return below instead of erroring.
    const isOauth1Callback = router.query.oauth_verifier !== undefined;
    if (isOauth1Callback && !pendingAuth) {
      reportLostAuthSession();
      return;
    }

    const callbackParameters =
      pendingAuth?.callbackParameters ?? DEFAULT_CALLBACK_PARAMETERS;

    const hasAllParams = callbackParameters.every(
      (param) => router.query[param] !== undefined
    );
    if (!hasAllParams) {
      return;
    }

    // OAuth 2.0 uses "code", OAuth 1.0a uses "oauth_verifier"
    const code = (router.query.code ?? router.query.oauth_verifier) as
      | string
      | undefined;

    // OAuth 2.0 encodes authorizationId in the state param;
    // OAuth 1.0a doesn't, so fall back to sessionStorage
    const state = router.query.state as string | undefined;
    const authorizationId = state
      ? decodeAuthorizationIdFromState(state)
      : (pendingAuth?.authorizationId ?? null);

    // The value the server validates against the state it issued, to prevent
    // CSRF / authorization-code injection. OAuth 2.0 returns it as `state`
    // (already read above); Bitbucket OAuth 1.0a returns it as `oauth_token`.
    const callbackState =
      state ?? (router.query.oauth_token as string | undefined);

    if (!code) {
      // No `code`/`oauth_verifier`: not an OAuth callback we handle (e.g. an
      // ordinary popup navigation), so stay silent.
      return;
    }
    if (!authorizationId || !callbackState) {
      // A real callback arrived but we can't assemble the exchange (e.g. an
      // undecodable OAuth 2.0 `state`). Surface it instead of hanging.
      reportLostAuthSession();
      return;
    }

    safeSessionStorage.removeItem(SCM_AUTH_SESSION_KEY);

    hasExchangedRef.current = true;

    // Exchange the authorization code
    exchangeCode({
      variables: {
        input: {
          authorizationId,
          code,
          state: callbackState,
          redirectUri: window.location.origin
        }
      }
    })
      .then((result) => {
        const exchangeResult = result.data?.exchangeAuthorizationCode;

        if (exchangeResult?.success) {
          sendMessageToOpener({
            type: 'scm-auth-success',
            origin: ''
          });
          // Close popup after short delay
          setTimeout(() => {
            window.close();
          }, 500);
        } else {
          const errorMsg =
            exchangeResult?.error || 'Failed to complete authorization';
          sendMessageToOpener({
            type: 'scm-auth-error',
            origin: '',
            error: errorMsg
          });
        }
      })
      .catch((err: Error) => {
        const errorMsg = err.message || 'An unexpected error occurred';
        sendMessageToOpener({
          type: 'scm-auth-error',
          origin: '',
          error: errorMsg
        });
      });
  }, [router.isReady, router.query, exchangeCode]);

  // This component doesn't render anything
  return null;
};
